/**
 * FfSelectPortal - Performance-optimized portal for ff-select dropdowns
 *
 * This custom element acts as a single portal container for all ff-select dropdowns,
 * preventing overflow issues while maintaining excellent performance.
 *
 * Features:
 * - One portal for all selects (minimal DOM overhead)
 * - Shared CSS classes with ff-select
 * - Handles input changes automatically
 * - Dynamically positions based on triggering select
 * - Tracks position even during scroll, resize, or transform animations (e.g. Swiper)
 */

class FfSelectPortal extends HTMLElement {
  constructor() {
    super();

    // Internal state for rAF tracking
    this._tracking = false;
    this._lastRect = null;
    this._rafId = null;
  }

  /**
   * Show portal for a specific ff-select element
   * @param {HTMLElement} selectElement - The ff-select custom element
   */
  ffShow(selectElement) {
    this.currentSelect = selectElement;
    this.configClass && this.classList.remove(this.configClass);
    this.configClass = selectElement.dataset.ffSelectConfigClass;
    this.configClass && this.classList.add(this.configClass);
    const optionsContainer = selectElement.querySelector(
      ".ff-select-options-container"
    );
    if (!optionsContainer) return;

    // Clone options to portal
    this.innerHTML = optionsContainer.outerHTML;

    const originalOptions =
      optionsContainer.querySelectorAll(".ff-select-option");

    this.querySelectorAll(".ff-select-option").forEach((option, idx) => {
      option.addEventListener("click", () => {
        originalOptions[idx].click();
      });
    });

    // Initial positioning
    this.position();

    // Start tracking movement
    this.startTracking();
  }

  /**
   * Position the portal based on current select
   */
  position() {
    if (!this.currentSelect) return;

    const selectOptionsContainer = this.currentSelect.querySelector(".ff-select-options-container");

    const selectRect = this.currentSelect.getBoundingClientRect();
    const rect = selectOptionsContainer.getBoundingClientRect();

    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - selectRect.bottom;
    const spaceAbove = selectRect.top;

    // Determine if we should open upward or downward
    const optionsHeight = selectOptionsContainer.scrollHeight;

    const shouldOpenUpward =
      spaceBelow < optionsHeight && spaceAbove > spaceBelow;

    // Set horizontal position and width
    this.style.left = `${rect.left}px`;
    this.style.minWidth = `${rect.width}px`;

    // Set vertical position
    if (shouldOpenUpward) {
      this.style.top = "auto";
      this.style.bottom = `${viewportHeight - this.currentSelect.getBoundingClientRect().top + 6}px`;
    } else {
      this.style.top = `${rect.top + 4}px`;
      this.style.bottom = "auto";
    }
  }

  /**
   * Start per-frame tracking of the select's position
   */
  startTracking() {
    if (this._tracking) return;
    this._tracking = true;
    this._lastRect = this.currentSelect?.getBoundingClientRect() || null;
    this._rafId = requestAnimationFrame(this.trackPosition.bind(this));
  }

  /**
   * Stop tracking
   */
  stopTracking() {
    this._tracking = false;
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /**
   * Per-frame check for movement
   */
  trackPosition() {
    if (!this._tracking || !this.currentSelect) return;

    const rect = this.currentSelect.getBoundingClientRect();
    const last = this._lastRect;

    // Detect meaningful movement
    if (
      !last ||
      Math.round(rect.top) !== Math.round(last.top) ||
      Math.round(rect.left) !== Math.round(last.left)
    ) {
      this.position();
      this._lastRect = rect;
    }

    this._rafId = requestAnimationFrame(this.trackPosition.bind(this));
  }

  /**
   * Hide portal
   */
  ffHide() {
    this.currentSelect = null;
    this.innerHTML = "";
    this.stopTracking();
  }

  /**
   * Cleanup when removed
   */
  disconnectedCallback() {
    this.stopTracking();
  }
}

// Define custom element
if (!customElements.get("ff-select-portal")) {
  customElements.define("ff-select-portal", FfSelectPortal);
}
